import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Typography, Tabs, Tab, Button, Grid, 
  Card, CardContent, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress, Alert, TextField, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { DEMO_MODELS } from '../../helpers/MockData';
import PageLoading from '../../helpers/PageLoading';
import KeyManager from './KeyManager';
import RAGUpload from './RAGUpload';
import ApiUsage from './ApiUsage';
import ChatTab from './Chat';

import { getModels, setApiKey, setApiEndpoint, getApiKeys, getFiles, getKeyUsageStats, getConfig, updateConfig } from '../../api/services/agent-studio/AgentFeatures';
import { getAgentById, getAvailableEndpoints } from '../../api/services/agent-studio/AgentManagement';
import { assignUserToAgent, getAgentUsers, removeUserFromAgent, getMyAgents } from '../../api/services/agent-studio/AgentUserManagement';
import { getAllUsers, getCurrentUser } from '../../api/services/userService/User';
import { useKeycloak } from '@react-keycloak/web';
import { useTheme } from '@mui/material/styles';

const ManageAgents = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [agent, setAgent] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [nodes, setNodes] = useState({});

  useEffect(() => {
    const fetchEndpoints = async () => {
      try {
        await keycloak.updateToken(30);
        const response = await getAvailableEndpoints(keycloak.token);
        if (response.success && response.data) {
          setEndpoints(response.data.endpoints || []);
          setNodes(response.data.nodes || {});
        }
      } catch (error) {
        console.error("Error fetching endpoints:", error);
      }
    };

    fetchEndpoints();
  }, [keycloak]);

  useEffect(() => {
    const fetchAgent = async () => {
      try {
        await keycloak.updateToken(30);
        const token = keycloak.token;
        
        const currentUserResponse = await getCurrentUser(token);
        const currentUserId = currentUserResponse.success && currentUserResponse.user ? currentUserResponse.user.id : null;
        
        let isAssigned = false;
        let assignedAgentData = null;
        
        if (currentUserId) {
          try {
            const myAgentsResponse = await getMyAgents(token, currentUserId);
            if (myAgentsResponse.success && myAgentsResponse.agents) {
              const assignedAgent = myAgentsResponse.agents.find(
                (myAgent) => {
                  const agent = myAgent.agent || myAgent;
                  return String(agent.id) === String(agentId);
                }
              );
              
              if (assignedAgent) {
                isAssigned = true;
                assignedAgentData = assignedAgent;
                console.log('Found assigned agent:', assignedAgent);
              }
            }
          } catch (myAgentsError) {
            console.error('Error checking assigned agents:', myAgentsError);
          }
        }
        
        if (isAssigned && assignedAgentData) {
          const agent = assignedAgentData.agent || assignedAgentData;
          const endpoint = agent.endpoint || '';
          const apiKey = assignedAgentData.apiKey || assignedAgentData.api_key || '';
          
          console.log('Agent found via getMyAgents:', {
            agentId: agent.id,
            agentName: agent.name,
            endpoint,
            hasApiKey: !!apiKey,
            apiKeyLength: apiKey ? apiKey.length : 0
          });

          let configData = {};
          if (apiKey && endpoint) {
            try {
              const configResponse = await getConfig(apiKey, endpoint);
              configData = configResponse.data || configResponse;
              console.log('Config fetched successfully for assigned agent');
            } catch (configError) {
              console.warn('Error fetching config for assigned agent, using fallback data:', configError);
            }
          } else {
            console.warn('No API key or endpoint available for assigned agent:', { apiKey: !!apiKey, endpoint });
          }

          if (apiKey) {
            setApiKey(apiKey);
          }
          if (endpoint) {
            setApiEndpoint(endpoint);
            if (apiKey) {
              fetchModelsForEndpoint(endpoint, apiKey);
            }
          }

          setAgent({
            id: agent.id,
            name: agent.name,
            is_active: typeof agent.isActive === 'boolean' ? agent.isActive : false,
            status: agent.isActive ? 'active' : 'inactive',
            type: 'AI Agent',
            models: configData.models || agent.models || [],
            model: configData.generating_model || agent.model || '',
            endpoint: endpoint,
            language: configData.language || agent.language || 'en',
            system_prompt: configData.system_prompt || agent.system_prompt || '',
            domain: "main",
            apiKey: apiKey,
            createdAt: agent.createdAt || configData.created_at || '',
            updatedAt: agent.updatedAt || configData.updated_at || ''
          });
        } else {
          console.log('Agent not found in getMyAgents, trying getAgentById');
          const response = await getAgentById(token, agentId);
          if (response.success && response.data) {
            const agent = response.data.agent;
            const endpoint = agent.endpoint || '';
            const finalApiKey = agent.apiKey || agent.api_key || '';
            
            console.log('Agent fetched via getAgentById (no API key available):', {
              agentId: agent.id,
              agentName: agent.name,
              endpoint,
              hasApiKey: !!finalApiKey
            });
            
            if (!finalApiKey) {
              console.warn('API key not available for this agent. Please use getMyAgents to access agents with API keys.');
            }

            setAgent({
              id: agent.id,
              name: agent.name,
              is_active: typeof agent.isActive === 'boolean' ? agent.isActive : false,
              status: agent.isActive ? 'active' : 'inactive',
              type: 'AI Agent',
              models: agent.models || [],
              model: agent.model || '',
              endpoint: endpoint,
              language: agent.language || 'en',
              system_prompt: agent.system_prompt || '',
              domain: "main",
              apiKey: finalApiKey,
              createdAt: agent.createdAt || '',
              updatedAt: agent.updatedAt || ''
            });

            if (finalApiKey) {
              setApiKey(finalApiKey);
              if (endpoint) {
                setApiEndpoint(endpoint);
                fetchModelsForEndpoint(endpoint, finalApiKey);
              }
            }
          } else {
            console.error('Failed to fetch agent:', response.error);
            navigate('/agent-studio');
          }
        }
      } catch (error) {
        console.error('Error in fetchAgent:', error);
        navigate('/agent-studio');
      }
    };
    fetchAgent();
  }, [agentId, keycloak, navigate]);
  
  const fetchModelsForEndpoint = async (endpoint, apiKey) => {
    try {
      const response = await getModels(apiKey || "dummy-key", endpoint);
      if (response.data) {
        const modelList = response.data.map(model => ({
          id: model.key,
          name: model.key.split('/').pop(),
          value: model.key,
          provider: model.key.split('/')[0]
        }));
        setAvailableModels(modelList);
      }
    } catch (error) {
      console.error("Error fetching models for endpoint:", error);
      setAvailableModels(DEMO_MODELS);
    }
  };

  if (!agent) {
    return <PageLoading />;
  }

  return (
    <Box sx={{ px: 3, pt: 2, pb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Manage Agent: {agent.name}</Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/agent-studio')}
            sx={{ 
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              }
            }}
          >
            Exit
          </Button>
        </Stack>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={(e, v) => setActiveTab(v)} 
        sx={{ 
          mb: 3,
          '& .MuiTab-root': {
            color: 'text.secondary',
            '&.Mui-selected': {
              color: theme.palette.primary.main
            }
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main
          }
        }}
      >
        <Tab label="OVERVIEW" />
        <Tab label="CONFIGURATION" />
        <Tab label="API KEYS" />
        <Tab label="DOCUMENTS" />
        <Tab label="CHAT" />
        <Tab label="USAGE" />
        <Tab label="SHARE" />
      </Tabs>

      {activeTab === 0 && (
        <AgentOverview 
          agent={agent} 
          endpoints={endpoints}
          nodes={nodes}
          models={availableModels}
        />
      )}

      {activeTab === 1 && (
        <ConfigTab 
          apiKey={agent.apiKey}
          endpoint={agent.endpoint}
        />
      )}

      {activeTab === 2 && (
        <KeyManager 
          apiKey={agent.apiKey} 
          agentId={agent.id} 
          endpoint={agent.endpoint}
        />
      )}

      {activeTab === 3 && (
        <RAGUpload 
          agentId={agent.id}
          apiKey={agent.apiKey}
          endpoint={agent.endpoint}
        />
      )}

      {activeTab === 4 && (
        <ChatTab 
          model={agent.model || (availableModels.length > 0 ? availableModels[0].value : "mistralai/Mistral-7B-Instruct-v0.3")}
          agentId={agent.id}
          endpoint={agent.endpoint}
          apiKey={agent.apiKey}
          availableModels={availableModels}
        />
      )}

      {activeTab === 5 && (
        <ApiUsage 
          agentId={agent.id}
          apiKey={agent.apiKey}
          endpoint={agent.endpoint}
        />
      )}

      {activeTab === 6 && (
        <AgentUsersTab 
          agentId={agent.id}
          token={keycloak.token}
        />
      )}
    </Box>
  );
};

const ConfigTab = ({ apiKey, endpoint }) => {
  const theme = useTheme();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPromptExpanded, setIsPromptExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const isEditingRef = useRef(false);
  const [editedConfig, setEditedConfig] = useState({
    language: '',
    temperature: '',
    top_k: '',
    system_prompt: ''
  });

  useEffect(() => {
    const fetchConfig = async () => {
      if (!apiKey) {
        setError('API key is required to fetch configuration');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        console.log('ConfigTab - calling getConfig with:', { apiKey: apiKey ? 'present' : 'missing', endpoint });
        const response = await getConfig(apiKey, endpoint);
        const configData = response.data || response;
        setConfig(configData);
        if (!isEditingRef.current) {
          setEditedConfig({
            language: configData.language || '',
            temperature: typeof configData.temperature === 'number' ? configData.temperature.toFixed(1) : 
                        (configData.temperature !== undefined && configData.temperature !== null ? configData.temperature.toString() : ''),
            top_k: configData.top_k !== undefined && configData.top_k !== null ? configData.top_k.toString() : '',
            system_prompt: configData.system_prompt || ''
          });
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [apiKey, endpoint]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const newTemperature = parseFloat(editedConfig.temperature);
      const newTopK = parseInt(editedConfig.top_k);
      
      console.log('Config save - parsed values:', { newTemperature, newTopK, currentTemp: config.temperature, currentTopK: config.top_k });
      
      if (editedConfig.temperature && (!isNaN(newTemperature) && (newTemperature < 0 || newTemperature > 2.0))) {
        setError('Temperature must be between 0.0 and 2.0');
        setSaving(false);
        return;
      }
      
      if (editedConfig.top_k && (!isNaN(newTopK) && (newTopK < 1 || newTopK > 100))) {
        setError('Top K must be between 1 and 100');
        setSaving(false);
        return;
      }
      
      // Since the API resets fields not included in PATCH, we need to send ALL current values
      // to prevent other fields from being reset to defaults
      const updateData = {
        language: editedConfig.language || config.language || 'en',
        temperature: !isNaN(newTemperature) ? parseFloat(newTemperature.toFixed(1)) : parseFloat(config.temperature || 0),
        top_k: !isNaN(newTopK) ? newTopK : parseInt(config.top_k || 0),
        system_prompt: editedConfig.system_prompt || config.system_prompt || ''
      };
      
      console.log('Sending updateData (all values to prevent API resets):', JSON.stringify(updateData, null, 2));
      
      const updateResponse = await updateConfig(updateData, apiKey, endpoint);
      
      const response = await getConfig(apiKey, endpoint);
      const configData = response.data || response;
      console.log('Config after update:', {
        temperature: configData.temperature,
        top_k: configData.top_k,
        language: configData.language
      });
      setConfig(configData);
      
      setEditedConfig({
        language: configData.language || '',
        temperature: typeof configData.temperature === 'number' ? configData.temperature.toFixed(1) : 
                    (configData.temperature !== undefined && configData.temperature !== null ? configData.temperature.toString() : ''),
        top_k: configData.top_k !== undefined && configData.top_k !== null ? configData.top_k.toString() : '',
        system_prompt: configData.system_prompt || ''
      });
      
      setIsEditing(false);
      isEditingRef.current = false;
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedConfig({
      language: config.language || '',
      temperature: typeof config.temperature === 'number' ? config.temperature.toFixed(1) : 
                  (config.temperature !== undefined && config.temperature !== null ? config.temperature.toString() : ''),
      top_k: config.top_k !== undefined && config.top_k !== null ? config.top_k.toString() : '',
      system_prompt: config.system_prompt || ''
    });
    setIsEditing(false);
    isEditingRef.current = false;
  };

  const handleEdit = () => {
    setIsEditing(true);
    isEditingRef.current = true;
    setError(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="h6">Error loading configuration</Typography>
        <Typography>{error}</Typography>
      </Alert>
    );
  }

  if (!config) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No configuration data available
      </Typography>
    );
  }

  const cardStyle = {
    backgroundColor: 'background.paper',
    border: `1px solid divider`,
    borderRadius: 2,
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Agent Configuration
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isEditing ? (
            <Button
              variant="contained"
              onClick={handleEdit}
              sx={{ 
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark }
              }}
            >
              Edit Configuration
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                sx={{ 
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                  color: 'text.primary',
                  '&:hover': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)' }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ 
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark }
                }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
        <Card sx={cardStyle}>
          <CardContent sx={{ p: 3, flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
              Basic Information
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr', 
              gap: 2.5
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Agent ID
                </Typography>
                <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {config.agent_id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Language
                </Typography>
                {isEditing ? (
                  <FormControl fullWidth sx={{ mt: 0.5 }} size="small">
                    <Select
                      value={editedConfig.language}
                      onChange={(e) => setEditedConfig(prev => ({ ...prev, language: e.target.value }))}
                      sx={{ 
                        color: 'text.primary',
                        fontSize: '0.9rem',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'divider'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.palette.primary.main
                        }
                      }}
                    >
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="de">German</MenuItem>
                      <MenuItem value="multi-lingual">Multi-lingual</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                    {config.language === 'en' ? 'English' : 
                     config.language === 'de' ? 'German' : 
                     config.language === 'multi-lingual' ? 'Multi-lingual' : 
                     'Not specified'}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Created At
                </Typography>
                <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                  {formatDate(config.created_at)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Last Updated
                </Typography>
                <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                  {formatDate(config.updated_at)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card sx={cardStyle}>
          <CardContent sx={{ p: 3, flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
              Behavior Settings
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr', 
              gap: 2.5
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Temperature
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editedConfig.temperature}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Replace comma with dot for decimal separator
                      value = value.replace(',', '.');
                      // Only allow valid decimal numbers within reasonable range
                      if (value === '' || (/^\d*\.?\d*$/.test(value) && (value === '' || parseFloat(value) <= 2.0))) {
                        setEditedConfig(prev => ({ ...prev, temperature: value }));
                      }
                    }}

                    placeholder="0.0 - 2.0"
                    helperText="Valid range: 0.0 - 2.0"
                    sx={{ 
                      mt: 0.5,
                      '& .MuiInputBase-input': {
                        color: theme.palette.warning.main,
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'text.secondary',
                        fontSize: '0.7rem'
                      }
                    }}
                  />
                ) : (
                  <Typography sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.9rem', mt: 0.5, fontFamily: 'monospace' }}>
                    {typeof config.temperature === 'number' ? config.temperature.toFixed(1) : config.temperature}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Top K
                </Typography>
                {isEditing ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={editedConfig.top_k}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Only allow positive integers within valid range
                      if (value === '' || (/^\d+$/.test(value) && (value === '' || parseInt(value) <= 100))) {
                        setEditedConfig(prev => ({ ...prev, top_k: value }));
                      }
                    }}

                    placeholder="1 - 100"
                    helperText="Valid range: 1 - 100"
                    sx={{ 
                      mt: 0.5,
                      '& .MuiInputBase-input': {
                        color: theme.palette.info.main,
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'divider'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'text.secondary',
                        fontSize: '0.7rem'
                      }
                    }}
                  />
                ) : (
                  <Typography sx={{ color: 'info.main', fontWeight: 500, fontSize: '0.9rem', mt: 0.5, fontFamily: 'monospace' }}>
                    {config.top_k}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Rate Limit
                </Typography>
                <Typography sx={{ color: config.rate_limit > 0 ? 'error.main' : 'success.main', fontWeight: 500, fontSize: '0.9rem', mt: 0.5, fontFamily: 'monospace' }}>
                  {config.rate_limit || 'Unlimited'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Audio Input
                </Typography>
                <Chip 
                  label={config.audio_input_enabled ? "ENABLED" : "DISABLED"}
                  sx={{ 
                    backgroundColor: config.audio_input_enabled ? theme.palette.success.main : theme.palette.error.main, 
                    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.white, 
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: '24px',
                    mt: 0.5
                  }}
                  size="small"
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Card sx={cardStyle}>
          <CardContent sx={{ p: 3, flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
              Model Configuration
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr', 
              gap: 2.5
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Generating Model
                </Typography>
                <Typography sx={{ color: theme.palette.primary.main, fontWeight: 500, fontSize: '0.85rem', mt: 0.5, wordBreak: 'break-word' }}>
                  {config.generating_model || 'Not specified'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Embedding Model
                </Typography>
                <Typography sx={{ color: theme.palette.primary.main, fontWeight: 500, fontSize: '0.85rem', mt: 0.5, wordBreak: 'break-word' }}>
                  {config.embedding_model || 'Not specified'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                  Speech Model
                </Typography>
                <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                  {config.speech_model || 'Not configured'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={cardStyle}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
              Available Models ({config.models ? config.models.length : 0})
            </Typography>
            <Box sx={{ 
              maxHeight: config.models && config.models.length > 6 ? '400px' : 'auto', 
              overflow: config.models && config.models.length > 6 ? 'auto' : 'visible',
              pr: config.models && config.models.length > 6 ? 1 : 0
            }}>
              {config.models && config.models.length > 0 ? (
                <Grid container spacing={1}>
                  {config.models.map((model, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'
                        }
                      }}>
                        <Typography sx={{ 
                          color: 'text.primary', 
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          wordBreak: 'break-word',
                          lineHeight: 1.3
                        }}>
                          {model}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No models configured
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={cardStyle}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                System Prompt
              </Typography>
              <Button
                size="small"
                onClick={() => setIsPromptExpanded(!isPromptExpanded)}
                sx={{ 
                  color: theme.palette.primary.main,
                  textTransform: 'none',
                  fontSize: '0.8rem'
                }}
              >
                {isPromptExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </Box>
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={isPromptExpanded ? 20 : 10}
                value={editedConfig.system_prompt}
                onChange={(e) => setEditedConfig(prev => ({ ...prev, system_prompt: e.target.value }))}
                placeholder="Enter system prompt..."
                sx={{ 
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    lineHeight: 1.6
                  },
                  '& .MuiInputBase-root': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.02)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'
                  },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main
                  }
                }}
              />
            ) : (
              <Box sx={{ 
                p: 3,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                minHeight: isPromptExpanded ? '500px' : '200px',
                maxHeight: isPromptExpanded ? 'none' : '400px',
                overflow: 'auto',
                transition: 'all 0.3s ease'
              }}>
                <Typography sx={{ 
                  color: 'text.primary', 
                  fontSize: '0.85rem',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace'
                }}>
                  {config.system_prompt || 'No system prompt configured'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {(config.notification_email || config.allowed_ips || config.integrations || config.tools || config.custom_config) && (
        <Grid item xs={12}>
          <Card sx={cardStyle}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
                Advanced Settings
              </Typography>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                gap: 3
              }}>
                {config.notification_email && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Notification Email
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {config.notification_email}
                    </Typography>
                  </Box>
                )}
                {config.allowed_ips && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Allowed IPs
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {config.allowed_ips || 'All IPs allowed'}
                    </Typography>
                  </Box>
                )}
                {config.integrations && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Integrations
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {config.integrations === '{}' ? 'None configured' : config.integrations}
                    </Typography>
                  </Box>
                )}
                {config.tools && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Tools
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {config.tools || 'None configured'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      )}
      </Grid>
    </Box>
  );
};

const AgentOverview = ({ agent, endpoints, nodes, models }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalKeys: 0,
    totalRequests: 0,
    totalTokens: 0,
    totalDocuments: 0,
    loading: true
  });
  const statsFetchState = useRef({
    signature: '',
    timestamp: 0,
    inFlight: false
  });

  useEffect(() => {
    let cancelled = false;
    const fetchDashboardStats = async () => {
      const signature = `${agent.id || ''}-${agent.apiKey}-${agent.endpoint}`;
      const now = Date.now();
      if (statsFetchState.current.inFlight) {
        return;
      }
      if (statsFetchState.current.signature === signature && now - statsFetchState.current.timestamp < 60000) {
        return;
      }
      statsFetchState.current = { signature, timestamp: now, inFlight: true };
      setDashboardStats(prev => ({ ...prev, loading: true }));
      try {
        const [keysResponse, filesResponse] = await Promise.all([
          getApiKeys(agent.apiKey, agent.endpoint).catch(() => ({ data: { keys: [] } })),
          getFiles(agent.apiKey, 1, agent.endpoint).catch(() => ({ data: { files: [] } }))
        ]);

        const keys = keysResponse.data.keys || [];
        let totalRequests = 0;
        let totalTokens = 0;

        if (keys.length > 0) {
          const usagePromises = keys.map(key => 
            getKeyUsageStats(key.id, agent.apiKey, agent.endpoint).catch(() => ({ data: { total_requests: 0, total_tokens: 0 } }))
          );
          
          const usageResults = await Promise.all(usagePromises);
          totalRequests = usageResults.reduce((sum, result) => sum + (result.data.total_requests || 0), 0);
          totalTokens = usageResults.reduce((sum, result) => sum + (result.data.total_tokens || 0), 0);
        }

        let totalDocuments = 0;
        if (filesResponse.data && filesResponse.data.files) {
          let allFiles = [...filesResponse.data.files];
          let currentPage = 2;
          let hasMorePages = filesResponse.data.files.length > 0;

          while (hasMorePages) {
            try {
              const nextPageResponse = await getFiles(agent.apiKey, currentPage, agent.endpoint);
              const nextPageFiles = nextPageResponse.data.files;
              
              if (!nextPageFiles || nextPageFiles.length === 0) {
                hasMorePages = false;
              } else {
                allFiles = [...allFiles, ...nextPageFiles];
                currentPage++;
              }
            } catch (error) {
              hasMorePages = false;
            }
          }
          totalDocuments = allFiles.length;
        }

        if (!cancelled) {
          setDashboardStats({
            totalKeys: keys.length,
            totalRequests,
            totalTokens,
            totalDocuments,
            loading: false
          });
          statsFetchState.current = { signature, timestamp: Date.now(), inFlight: false };
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        if (!cancelled) {
          statsFetchState.current = { signature: '', timestamp: 0, inFlight: false };
          setDashboardStats(prev => ({ ...prev, loading: false }));
        }
      }
    };

    if (agent.apiKey && agent.endpoint) {
      fetchDashboardStats();
    }
    return () => {
      cancelled = true;
    };
  }, [agent.id, agent.apiKey, agent.endpoint]);

  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    navigate('/agent-studio');
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getNodeInfo = (endpoint) => {
    if (!endpoint || !nodes || !nodes[endpoint]) return null;
    return nodes[endpoint];
  };

  const nodeInfo = getNodeInfo(agent.endpoint);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={{ 
          backgroundColor: 'background.paper',
          border: `1px solid divider`,
          borderRadius: 2
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                  {agent.name}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                  <Chip 
                    label={agent.is_active ? "ACTIVE" : "INACTIVE"}
                    sx={{ 
                      backgroundColor: agent.is_active ? theme.palette.primary.main : theme.palette.warning.main, 
                      color: theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.common.black, 
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                    size="small"
                  />
                  <Chip 
                    label="Production"
                    sx={{ 
                      backgroundColor: theme.palette.success.main, 
                      color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.white, 
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                    size="small"
                  />
                </Stack>
              </Box>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
                  Configuration Details
                </Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 3,
                  p: 3,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Agent ID
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.id}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Type
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.type}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Primary Model
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.model ? agent.model.split('/').pop() : 'See CONFIGURATION tab'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Language
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {agent.language === 'en' ? 'English' : 'German'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Created
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {formatDate(agent.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                      Updated
                    </Typography>
                    <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                      {formatDate(agent.updatedAt)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
                  Infrastructure
                </Typography>
                {nodeInfo ? (
                  <Box sx={{ 
                    p: 3,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`
                  }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 3 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          Endpoint
                        </Typography>
                        <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {agent.endpoint}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          Location
                        </Typography>
                        <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {nodeInfo.location}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          GPU Type
                        </Typography>
                        <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {nodeInfo.gpu}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600 }}>
                          GPU Count
                        </Typography>
                        <Typography sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem', mt: 0.5 }}>
                          {nodeInfo.noOfGpu}x {nodeInfo.gpu}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    textAlign: 'center'
                  }}>
                    <Typography color="text.secondary">
                      No infrastructure information available
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: 'white', fontWeight: 600, mb: 2 }}>
          Quick Statistics
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
          backgroundColor: 'background.paper',
          border: `1px solid divider`,
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: theme.palette.primary.main, 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: theme.palette.primary.main }} />
                  ) : (
                    dashboardStats.totalKeys
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  API Keys
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Total active keys
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
          backgroundColor: 'background.paper',
          border: `1px solid divider`,
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: 'info.main', 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: 'info.main' }} />
                  ) : (
                    dashboardStats.totalRequests.toLocaleString()
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Requests
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  API calls made
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
          backgroundColor: 'background.paper',
          border: `1px solid divider`,
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: 'warning.main', 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: 'warning.main' }} />
                  ) : (
                    dashboardStats.totalTokens.toLocaleString()
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Tokens
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Tokens processed
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
          backgroundColor: 'background.paper',
          border: `1px solid divider`,
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h3" sx={{ 
                  color: 'success.main', 
                  fontWeight: 700,
                  mb: 1
                }}>
                  {dashboardStats.loading ? (
                    <CircularProgress size={32} sx={{ color: 'success.main' }} />
                  ) : (
                    dashboardStats.totalDocuments
                  )}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Documents
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  RAG documents
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this agent? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const AgentUsersTab = ({ agentId, token }) => {
  const theme = useTheme();
  const { keycloak } = useKeycloak();
  const [agentUsers, setAgentUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('user');
  const [userToRemove, setUserToRemove] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        await keycloak.updateToken(30);

        const currentUserResponse = await getCurrentUser(keycloak.token);
        if (currentUserResponse.success && currentUserResponse.user) {
          setCurrentUserId(currentUserResponse.user.id);
        }

        const clientId = currentUserResponse.user?.client?.client?.id;

        const [agentUsersResponse, allUsersResponse] = await Promise.all([
          getAgentUsers(keycloak.token, agentId, currentUserResponse.user?.id),
          getAllUsers({
            client_id: clientId,
            pagination: { page: 1, pageSize: 10000 },
            sorting: { sort_by: 'SORT_BY_NAME', order: 'SORT_ORDER_ASCENDING' }
          }, keycloak.token)
        ]);

        if (agentUsersResponse.success) {
          setAgentUsers(agentUsersResponse.users || []);
        } else {
          setError(agentUsersResponse.error || 'Failed to fetch agent users');
        }

        if (allUsersResponse.success) {
          setAllUsers(allUsersResponse.users || []);
        }
      } catch (err) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [agentId, keycloak]);

  const handleAssignUser = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }

    try {
      setAssigning(true);
      setError(null);
      await keycloak.updateToken(30);

      const response = await assignUserToAgent(keycloak.token, selectedUserId, agentId, selectedRole);

      if (response.success) {
        setSuccessMessage(`User assigned successfully with role: ${selectedRole}`);
        setAssignDialogOpen(false);
        setSelectedUserId('');
        setSelectedRole('user');

        const agentUsersResponse = await getAgentUsers(keycloak.token, agentId, currentUserId);
        if (agentUsersResponse.success) {
          setAgentUsers(agentUsersResponse.users || []);
        }
      } else {
        setError(response.error || 'Failed to assign user');
      }
    } catch (err) {
      setError(err.message || 'Failed to assign user');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!userToRemove) return;

    try {
      setAssigning(true);
      setError(null);
      await keycloak.updateToken(30);

      const response = await removeUserFromAgent(keycloak.token, userToRemove, agentId);

      if (response.success) {
        setSuccessMessage('User removed successfully');
        setRemoveDialogOpen(false);
        setUserToRemove(null);

        const agentUsersResponse = await getAgentUsers(keycloak.token, agentId, currentUserId);
        if (agentUsersResponse.success) {
          setAgentUsers(agentUsersResponse.users || []);
        }
      } else {
        setError(response.error || 'Failed to remove user');
      }
    } catch (err) {
      setError(err.message || 'Failed to remove user');
    } finally {
      setAssigning(false);
    }
  };

  const getUserDisplayName = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    if (user) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || userId;
    }
    return userId;
  };

  const getUserEmail = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    return user?.email || '';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  const assignedUserIds = new Set(agentUsers);
  const availableUsers = allUsers.filter(user => !assignedUserIds.has(user.id));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Share ({agentUsers.length})
        </Typography>
        <Button
          variant="contained"
          onClick={() => setAssignDialogOpen(true)}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark }
          }}
        >
          Assign User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {agentUsers.length === 0 ? (
        <Card sx={{ 
          backgroundColor: 'background.paper',
          border: `1px solid divider`,
          borderRadius: 2,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography color="text.secondary">
            No users assigned to this agent yet. Click "Assign User" to add users.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {agentUsers.map((userId) => (
            <Grid item xs={12} sm={6} md={4} key={userId}>
              <Card sx={{ 
                backgroundColor: 'background.paper',
                border: `1px solid divider`,
                borderRadius: 2
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 1 }}>
                        {getUserDisplayName(userId)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {getUserEmail(userId) || 'No email'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                        ID: {userId}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setUserToRemove(userId);
                        setRemoveDialogOpen(true);
                      }}
                      sx={{ ml: 2 }}
                    >
                      Remove
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign User to Agent</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label="Select User"
              >
                {availableUsers.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                label="Role"
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignUser} 
            variant="contained" 
            disabled={assigning || !selectedUserId}
            sx={{ backgroundColor: theme.palette.primary.main }}
          >
            {assigning ? <CircularProgress size={20} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove User from Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{getUserDisplayName(userToRemove)}</strong> from this agent? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button 
            onClick={handleRemoveUser} 
            color="error" 
            variant="contained"
            disabled={assigning}
          >
            {assigning ? <CircularProgress size={20} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageAgents;
